import { useEffect, useState } from "react";
import {
  FormProvider, SubmitHandler, useFieldArray, useForm
} from "react-hook-form";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import useFetch from "../../../hooks/useFetch";
import { ICityPageConfigCreateDto } from "../../../interfaces/endpoints/ICityPage";
import {
  getCityPageConfig, updateCityPageConfig
} from "../../../services/CityPages.service";
import Localizations from "../../bundles/Localizations";
import FormWrapper from "../../ui/FormWrapper";
import Input from "../../ui/input/Input";
import SpinnerLoader from "../../ui/SpinnerLoader";

const EditCityPageSEO = () => {
  const { id } = useParams<{ id: string }>();
  const methods = useForm<ICityPageConfigCreateDto>({
    defaultValues: {
      cityPageId: Number(id),
      locales: [
        { languageId: 1, title: "", description: "", keywords: "" },
        { languageId: 2, title: "", description: "", keywords: "" }
      ],
      imageUrl: ""
    }
  });

  const { reset, handleSubmit, control, register, formState } = methods;
  const { fields } = useFieldArray({ control, name: "locales", keyName: "uuid" });
  const [langIdActive, setLangIdActive] = useState<1 | 2>(1);

  const { response, loading, refetch } = useFetch(
    () => getCityPageConfig(Number(id)),
    { initRequest: true }
  );

  useEffect(() => {
    if (response?.data) reset(response.data);
  }, [response, reset]);

  const onSubmit: SubmitHandler<ICityPageConfigCreateDto> = async (data) => {
    try {
      await updateCityPageConfig(Number(id), data);
      toast.success("Konfigurimi SEO u përditësua me sukses.", { position: "top-right" });
      refetch();
    } catch {
      toast.error("Ndodhi një gabim gjatë përditësimit të SEO.", { position: "top-right" });
    }
  };

  return (
    <FormProvider {...methods}>
      <FormWrapper
        onSubmit={handleSubmit(onSubmit)}
        title="Modifiko Konfigurimin SEO"
        showPrompt={
          !formState.isSubmitSuccessful &&
          !!Object.keys(formState.dirtyFields).length
        }
      >
        <div className="min-h-[350px] flex items-center justify-center">
          {loading ? (
            <SpinnerLoader className="text-primary" />
          ) : (
            <div className="flex flex-col w-full">
              <Input
                {...register("imageUrl")}
                label="Image URL"
                className="my-2"
                placeholder="Shkruaj URL e imazhit... (do të marrë vlerën default nëse është bosh)"
                error={formState.errors.imageUrl?.message}
              />
              <Localizations
                activeId={langIdActive}
                onChange={(l) => setLangIdActive(l)}
                alError={!!formState.errors.locales?.[1]?.title}
                enError={!!formState.errors.locales?.[0]?.title}
              />
              <div className="overflow-hidden">
                <div className={
                  "transform duration-500 transition-all w-full flex "
                  + (langIdActive === 1 ? "translate-x-0" : "-translate-x-full")
                }>
                  {fields.map((field, idx) => (
                    <div
                      key={field.uuid}
                      className={
                        "w-full flex-none p-[1px] duration-500 "
                        + `editor-container-${field.languageId} `
                        + (langIdActive === field.languageId
                          ? "opacity-100" : "opacity-0")
                      }
                    >
                      <Input
                        {...register(`locales.${idx}.title`)}
                        label="Title" className="my-2"
                        placeholder="Shkruaj titullin... (do të marrë vlerën default nëse është bosh)"
                        error={formState.errors.locales?.[idx]?.title?.message}
                      />
                      <Input
                        {...register(`locales.${idx}.description`)}
                        label="Description" className="my-2"
                        placeholder="Shkruaj përshkrimin... (do të marrë vlerën default nëse është bosh)"
                        error={formState.errors.locales?.[idx]?.description?.message}
                      />
                      <Input
                        {...register(`locales.${idx}.keywords`)}
                        label="Keywords" className="my-2"
                        placeholder="Shkruaj fjalët kyçe... (do të marrë vlerën default nëse është bosh)"
                        error={formState.errors.locales?.[idx]?.keywords?.message}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </FormWrapper>
    </FormProvider>
  );
};

export default EditCityPageSEO;
